import { useEffect } from "react";
import { setCookie } from "@/lib/cookie";
import {
  normalizeBrightness,
  normalizeTimeZone,
  persistBrightness,
  persistTimeZone,
} from "@/lib/display-preferences";
import { useAuth } from "@/hooks/useAuth";

function readStoredTheme(): "dark" | "light" {
  if (typeof document === "undefined") return "dark";
  if (document.documentElement.classList.contains("light")) return "light";
  if (typeof localStorage === "undefined") return "dark";
  return localStorage.getItem("theme") === "light" ? "light" : "dark";
}

// Keeps the document theme class, cookie, and local storage aligned with the
// current session preference. The pre-paint script in
// app/views/layouts/application.html.erb reads the cookie before React boots;
// chart helpers and Tailwind overrides read the `light` class on <html>.
//
// Lives above the AuthScopedTree remount boundary in App.tsx so it
// keeps observing `user` across login/logout/login transitions and
// applies user-B's preferences in the same effect tick that
// `setSession` flips identity.
export function useThemeClass() {
  const { user } = useAuth();
  const theme = user?.theme === "light" ? "light" : user?.theme ? "dark" : null;
  const displayBrightness = user?.metadata?.displayBrightness;
  const displayTimeZone = user?.metadata?.displayTimeZone;

  useEffect(() => {
    if (typeof document === "undefined") return;
    const root = document.documentElement;
    const nextTheme = theme ?? readStoredTheme();
    root.classList.toggle("light", nextTheme === "light");
    if (typeof localStorage !== "undefined") {
      localStorage.setItem("theme", nextTheme);
    }
    setCookie("theme", nextTheme);
  }, [theme, user?.id]);

  useEffect(() => {
    if (displayBrightness == null) return;
    persistBrightness(normalizeBrightness(displayBrightness));
  }, [displayBrightness]);

  useEffect(() => {
    if (displayTimeZone == null) return;
    persistTimeZone(normalizeTimeZone(displayTimeZone));
  }, [displayTimeZone]);
}
